import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.5;
  }
`;

const LoaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.theme === 'dark'
    ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
    : '#ffffff'};
  z-index: 9999;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${float} 3s ease-in-out infinite;
`;

const CircleWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  filter: drop-shadow(0 0 8px rgba(255, 111, 0, 0.3));
`;

const SVGCircle = styled.svg`
  width: 100%;
  height: 100%;
  animation: ${rotate} 2s linear infinite;
`;

const Circle = styled.circle`
  width: 100%;
  height: 100%;
  fill: none;
  stroke-width: 6;
  stroke-linecap: round;
  stroke: ${props => props.theme === 'dark' ? '#ff6f00' : '#ff8f00'};
  animation: ${dash} 1.5s ease-in-out infinite;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: ${props => props.theme === 'dark' ? '#ff6f00' : '#ff8f00'};
`;

const LoaderText = styled.div`
  margin-top: 30px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  background: ${props => props.theme === 'dark'
    ? 'linear-gradient(90deg, #ff6f00, #ff8f00, #ffab40)'
    : 'linear-gradient(90deg, #ff6f00, #ff8f00, #ffa726)'};
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${gradient} 3s linear infinite;
  text-align: center;
  filter: ${props => props.theme === 'dark'
    ? 'drop-shadow(0 2px 8px rgba(255, 111, 0, 0.3))'
    : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'};
`;

const GlowEffect = styled.div`
  position: absolute;
  width: 150%;
  height: 150%;
  background: radial-gradient(
    circle,
    ${props => props.theme === 'dark'
      ? 'rgba(255, 111, 0, 0.1)'
      : 'rgba(255, 143, 0, 0.1)'
    } 0%,
    transparent 70%
  );
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Loader = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <LoaderContainer theme={theme}>
      <LoaderWrapper>
        <GlowEffect theme={theme} />
        <CircleWrapper>
          <SVGCircle viewBox="0 0 50 50">
            <Circle cx="25" cy="25" r="20" theme={theme} />
          </SVGCircle>
          <IconContainer theme={theme}>
            🍳
          </IconContainer>
        </CircleWrapper>
        <LoaderText theme={theme}>
          {t('loader.preparing')}
        </LoaderText>
      </LoaderWrapper>
    </LoaderContainer>
  );
};

export default Loader; 